import { type CountryCode, type ProjectCode } from '@constants'
import type * as ApplePayJS from '@dni-types/payment/applepay-types'
import { type Nullable } from '../nullable'
import { type UserPaymentTokenInfo, type PaymentMethod, type PaymentMethodType } from './payment.ts'
export { type UserPaymentTokenInfo } from './payment.ts'

export enum ECommerceOrderType {
  CART = 'cart',
  TRANSACTION = 'transaction',
}

export enum ECommercePaymentTransactionsStatus {
  UNCREATED = 'uncreated',
  PENDING = 'pending',
  PAID = 'paid',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

enum ECommerceChildOrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  READY_FOR_SHIPMENT = 'ready-for-shipment',
}

export enum ReceiptType {
  BUSINESS_EINVOICE = 'business_einvoice',
  PHONE_CARRIER = 'phone_carrier',
  DONATE_INVOICE = 'donate_invoice',
  NON_BUSINESS_EINVOICE = 'non_business_einvoice',
}

export interface CheckoutPaySessionInfo {
  id: string
  payment_session_token: string
  expires_on: string
  accepted_payment_methods: string[]
  [key: string]: unknown
}

export interface PaymentServiceProviderInfo {
  app_id?: string
  app_key?: string
  ecpay_token?: string
  installment_options?: number[]
  app_pay_identifier?: string
  bank_list?: {
    name: string
    bank_code: string
  }[]
  checkoutpay_session_info?: CheckoutPaySessionInfo
}

export interface CkoPaymentSession {
  id: string
  payment_session_token: string
  [key: string]: unknown
}

export interface ECommercePayment {
  payment_transaction_id: number
  payment_method: PaymentMethod
  /** 金流服務商資訊 */
  payment_service_provider_info: PaymentServiceProviderInfo
  user_payment_token_infos: UserPaymentTokenInfo[]
  payment_session?: CkoPaymentSession
}

export interface ServicePayInfo {
  bank_code?: Nullable<string>
  pay_token?: Nullable<string>
  apple_pay_info?: Nullable<{
    paymentData: ApplePayJS.ApplePayPaymentToken['paymentData']
  }>
  card_identifier?: Nullable<string>
}

export type PaymentTransactionId = Nullable<string | number>
export type PaymentOrderHash = Nullable<string>

export interface ECommercePaymentExecution {
  payment_transaction_id?: PaymentTransactionId
  service_pay_info?: Nullable<ServicePayInfo>
  user_payment_token_info?: UserPaymentTokenInfo
  payment_success_url?: string
  payment_fallback_url?: string
  payment_option_id?: number
  cko_payment_id?: string
}

export interface ECommercePaymentChildOrder {
  id: number
  order_id: string
  project_code: ProjectCode
  country_code: CountryCode
  status: ECommerceChildOrderStatus
  customer_id: number
  customer_note: string
  delivery_reason: string
  shipping_method: string
  shipping_method_title: string
  discount_total: number
  shipping_total: number
  total: number
  shipping: {
    id: number
    order_id: number
    sequence_index: number
    status: string
    shipping_date: string
    billing_address_email: string
    billing_address_phone: string
    billing_address_company: string
    billing_address_postcode: string
    billing_address_country: string
    billing_address_state: string
    billing_address_city: string
    billing_address_meta: {
      last_name: string
      first_name: string
      address1: string
      address2: string
    }
    shipping_address_company: string
    shipping_address_postcode: string
    shipping_address_country: string
    shipping_address_state: string
    shipping_address_city: string
    shipping_address_meta: {
      last_name: string
      first_name: string
      address1: string
      address2: string
    }
    created_at: string
    updated_at: string
    deleted_at: string
  }
  order_items: {
    project_code: ProjectCode
    id: string
    product_id: number
    variation_id: number
    sku: string
    stock: number
    original_price: number
    sale_price: number
    attributes: Record<string, string>
    weight_attribute: string
    is_out_of_stock: boolean
    delivery_class: string
    bundle_id: string
    is_addon: Nullable<boolean>
    limited_quantity: Nullable<number>
    quantity: number
    available_campaign_bundles: Nullable<{
      campaign_bundle_id: number
      display_name: string
      is_applied: boolean
    }>
  }
}

export interface ECommercePaymentTransactions {
  id: number
  status: ECommercePaymentTransactionsStatus
  total: number
  subtotal: number
  tax: number
  payment_method: PaymentMethodType
  shipping_total: number
  country_code: CountryCode
  currency: string
  customer_note: string
  delivery_date: string
  delivery_time_slot: string
  purchase_order_name: string
  receiver_name: string
  receiver_email: string
  receiver_phone: string
  shipping_methods: string[]
  shipping_address: string
  need_pdf: boolean
  posting_date_in_next_month: boolean
  refund_statement: boolean
  receipt_type: ReceiptType
  created_at: string
  invoice_option: {
    business_ubn_title: string
    business_ubn: string
    phone_carrier_number: string
    charity_organization: string
  }
  child_orders: ECommercePaymentChildOrder[]
  transaction_id: string
  order_hash: string
}

